import { useAuth as useClerkAuth, useUser } from "@clerk/react";
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";

import { trpc } from "../trpc";

/**
 * Admin auth + role context.
 *
 * Wraps Clerk for identity and the backend `adminMe` query for the
 * resolved role + application state. The role is what gates every
 * surface in this app — pages call `useAuth().role` rather than
 * `user.publicMetadata.role` so the source of truth is the verified
 * server response (matches what the backend's role guards see) and
 * the SUPERADMIN_EMAILS bootstrap path stays consistent on both ends.
 */

/**
 * "coordinator" is the retired Coordination role. Accounts that still hold
 * it keep signing in with a team member's access and are shown as a Team
 * member; nobody is given it any more.
 */
export type AdminRole = "member" | "coordinator" | "volunteer" | "superadmin";

interface ApplicationState {
  id: string;
  status: "pending" | "active" | "cancelled" | string;
  membershipType: string;
  submittedAt: string;
  fullName: string;
}

/** Which outside services the Worker has keys for (from adminMe). */
interface SetupState {
  email: boolean;
  payments: boolean;
}

const NOT_SET_UP: SetupState = { email: false, payments: false };

interface AuthValue {
  isAuthenticated: boolean;
  isLoaded: boolean;
  /** True once Clerk has resolved AND adminMe has returned (or errored
   *  to a stable null). Pages should wait on this before branching. */
  isResolved: boolean;
  user: { id: string; email: string | null; fullName: string | null } | null;
  /** Resolved role from the backend. Null = signed in but no role yet
   *  (fresh sign-up → application flow). */
  role: AdminRole | null;
  /** Member application row tied to this Clerk user, if any. */
  application: ApplicationState | null;
  /** Granted via SUPERADMIN_EMAILS rather than explicit Clerk metadata. */
  isBootstrap: boolean;
  /** Both false until adminMe has answered. */
  setup: SetupState;
  /** The role lookup failed. Distinct from "no role": a flaky request
   *  used to resolve to role=null, which routed an approved volunteer
   *  to the "nobody's given you access" screen as if revoked. */
  roleError: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Dev-bypass variant — no Clerk hooks (they throw without a
 * ClerkProvider). The local Worker's localhost-gated bypass answers
 * `adminMe`, so identity and role still come from the same verified
 * server response the real provider uses. Dev-only: the flag guarding
 * this tree is compile-time false in production builds.
 */
export function DevBypassAuthProvider({ children }: { children: ReactNode }) {
  const meQuery = trpc.adminMe.useQuery(undefined, {
    staleTime: 30_000,
    retry: 2,
  });

  const value = useMemo<AuthValue>(() => {
    const me = meQuery.data ?? null;
    return {
      isLoaded: true,
      isAuthenticated: true,
      isResolved: meQuery.isSuccess || meQuery.isError,
      user: {
        id: me?.userId ?? "dev-bypass",
        email: me?.email ?? null,
        fullName: me?.email ? `Dev bypass (${me.email})` : "Dev bypass",
      },
      // SAFETY: `me.role` is written by the backend from ADMIN_ROLES; the router types it as string.
      role: (me?.role ?? null) as AdminRole | null,
      // SAFETY: `me.application` is written by the backend from the application state union; the router types it as string.
      application: (me?.application ?? null) as ApplicationState | null,
      isBootstrap: me?.isBootstrap ?? false,
      setup: me?.setup ?? NOT_SET_UP,
      roleError: meQuery.isError,
      signOut: async () => {
        window.location.reload();
      },
    };
  }, [meQuery.data, meQuery.isSuccess, meQuery.isError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded: clerkLoaded, isSignedIn, signOut } = useClerkAuth();
  const { user } = useUser();

  // Only query adminMe once Clerk has settled and the user is signed
  // in. Otherwise the Bearer token is empty and every call 401s.
  const meQuery = trpc.adminMe.useQuery(undefined, {
    enabled: clerkLoaded && Boolean(isSignedIn),
    staleTime: 30_000,
    retry: 2,
  });

  const value = useMemo<AuthValue>(() => {
    const me = meQuery.data ?? null;
    const isLoaded = clerkLoaded;
    const isAuthenticated = !!isSignedIn;
    const isResolved =
      isLoaded &&
      (!isAuthenticated ||
        meQuery.isSuccess ||
        meQuery.isError ||
        !meQuery.isLoading);

    return {
      isLoaded,
      isAuthenticated,
      isResolved,
      user: user
        ? {
            id: user.id,
            email: user.primaryEmailAddress?.emailAddress ?? null,
            fullName:
              [user.firstName, user.lastName].filter(Boolean).join(" ") ||
              user.username ||
              null,
          }
        : null,
      // SAFETY: `me.role` is written by the backend from ADMIN_ROLES; the router types it as string.
      role: (me?.role ?? null) as AdminRole | null,
      // SAFETY: `me.application` is written by the backend from the application state union; the router types it as string.
      application: (me?.application ?? null) as ApplicationState | null,
      isBootstrap: me?.isBootstrap ?? false,
      setup: me?.setup ?? NOT_SET_UP,
      roleError: meQuery.isError,
      signOut: async () => {
        await signOut();
      },
    };
  }, [
    clerkLoaded,
    isSignedIn,
    user,
    signOut,
    meQuery.data,
    meQuery.isSuccess,
    meQuery.isError,
    meQuery.isLoading,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
